import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  Select,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  Th,
  VStack,
} from '@chakra-ui/react'
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { analyticsService } from '@/lib/services/analyticsService'
import type {
  TicketsOverviewParams,
  TicketsOverviewResponse,
  TicketsOverviewRowAgent,
  TicketsOverviewRowDetail,
  TicketsOverviewRowMttr,
  TicketsOverviewTableRow,
} from '@/lib/services/analyticsService'
import { apiClient } from '@/lib/api/client'
import {
  DISPLAY_EMPTY,
  formatAnalyticsCsvCell,
  sanitizeRestrictedLabelText,
} from '@/lib/utils/displayText'
import { getCsatScoreLabel } from '@/types/csat'

const DEFAULT_DATE_RANGE_DAYS = 30
const MTTR_DEFAULT_DATE_RANGE_DAYS = 90

function formatDateForInput(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function getDefaultDateRange(): { date_from: string; date_to: string } {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - DEFAULT_DATE_RANGE_DAYS)
  return {
    date_from: formatDateForInput(start),
    date_to: formatDateForInput(end),
  }
}

function getMttrDefaultDateRange(): { date_from: string; date_to: string } {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - MTTR_DEFAULT_DATE_RANGE_DAYS)
  return {
    date_from: formatDateForInput(start),
    date_to: formatDateForInput(end),
  }
}

type CategoryOption = { id: number; name: string; code: string }
type TicketStatusOption = { id: number; code: string; label: string }

function escapeCsvCell(val: string | number, options?: { restricted?: boolean }): string {
  const s = formatAnalyticsCsvCell(val, options)
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}

/** Division code for CSV — never use em-dash placeholder (Excel shows as â€"). */
function divisionCodeForCsv(r: TicketsOverviewRowDetail): string {
  const direct =
    r.division_code?.trim() ||
    r.department_code?.trim() ||
    r.department?.code?.trim()
  if (direct) {
    return formatAnalyticsCsvCell(direct, { restricted: true })
  }
  const divisionLabel = r.division?.trim()
  if (!divisionLabel) return ''
  return formatAnalyticsCsvCell(divisionLabel, { restricted: true })
}

function csatLabelForDisplay(r: TicketsOverviewRowDetail): string {
  const fromApi =
    r.csat_score_label != null && String(r.csat_score_label).trim() !== ''
      ? r.csat_score_label
      : null
  if (fromApi) {
    return sanitizeRestrictedLabelText(fromApi) || fromApi
  }
  if (r.csat_score != null && r.csat_score !== '') {
    const label = getCsatScoreLabel(r.csat_score)
    return label !== '—' ? label : ''
  }
  return ''
}

function csatLabelForCsv(r: TicketsOverviewRowDetail): string {
  const raw =
    r.csat_score_label != null && String(r.csat_score_label).trim() !== ''
      ? r.csat_score_label
      : getCsatScoreLabel(r.csat_score)
  return formatAnalyticsCsvCell(raw, { restricted: true })
}

/** CSV for chart summary: date, count */
function chartSummaryToCSV(labels: string[], data: number[]): string {
  const headers = ['Date', 'Count']
  const rows = labels.map((label, i) => [label, data[i] ?? 0].join(','))
  return [headers.join(','), ...rows].join('\r\n')
}

function formatDatetime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

function formatMttrSummaryHours(hours: number | null | undefined): string {
  if (hours == null || Number.isNaN(hours)) return '—'
  return `${hours.toFixed(2)}h`
}

function formatMttrSummaryMinutes(minutes: number | null | undefined): string {
  if (minutes == null || Number.isNaN(minutes)) return '—'
  return `${minutes.toFixed(0)}m`
}

function getTicketPriorityValue(row: TicketsOverviewRowDetail): string {
  const r = row as TicketsOverviewRowDetail & {
    ticket_priority?: { label?: string | null; level?: number | null } | null
  }
  if (r.ticket_priority_label) return r.ticket_priority_label
  if (r.priority) return r.priority
  if (r.ticket_priority?.label) return r.ticket_priority.label
  if (r.ticket_priority?.level != null) return `Priority ${r.ticket_priority.level}`
  return ''
}

function getTicketSeverityValue(row: TicketsOverviewRowDetail): string {
  const r = row as TicketsOverviewRowDetail & {
    sla_severity_label?: string | null
    sla?: { severity?: string | null } | null
  }
  return r.sla_severity_label ?? r.severity ?? r.sla?.severity ?? ''
}

function formatSeverityLabel(value: string | null | undefined): string {
  if (!value) return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  return /^severity\b/i.test(trimmed) ? trimmed : `Severity ${trimmed}`
}

const RESOLVED_OR_CLOSED_STATUS_CODES = new Set(['resolved', 'closed'])

function isResolvedOrClosedTicket(row: TicketsOverviewRowDetail): boolean {
  const code = row.status_code?.trim().toLowerCase()
  if (code && RESOLVED_OR_CLOSED_STATUS_CODES.has(code)) {
    return true
  }
  const label = row.status_label?.trim().toLowerCase()
  return label === 'resolved' || label === 'closed'
}

/** SLA status for analytics display; resolved/closed tickets show completed. */
function slaStatusForDisplay(row: TicketsOverviewRowDetail): string {
  if (isResolvedOrClosedTicket(row)) {
    return 'completed'
  }
  return row.sla_clock_status?.trim() || DISPLAY_EMPTY
}

/** Division code from analytics API (resolved server-side from departments catalog). */
function divisionCodeForDisplay(r: TicketsOverviewRowDetail): string {
  const direct =
    r.division_code?.trim() ||
    r.department_code?.trim() ||
    r.department?.code?.trim()
  if (direct) {
    const decoded = sanitizeRestrictedLabelText(direct)
    return decoded || DISPLAY_EMPTY
  }
  const divisionLabel = r.division?.trim()
  if (!divisionLabel) return DISPLAY_EMPTY
  const decoded = sanitizeRestrictedLabelText(divisionLabel)
  return decoded || DISPLAY_EMPTY
}

/** CSV for details table (ticket request + status + SLA clock) */
function detailsTableToCSV(rows: TicketsOverviewRowDetail[]): string {
  const headers = [
    'ID',
    'Request Number',
    'Service Category',
    'Category',
    'Sub-Category',
    'Item',
    'Status',
    'Technician',
    'Priority',
    'Severity',
    'Division code',
    'Location',
    'Contact Name',
    'Created By',
    'Contact Email',
    'Contact Number',
    'Created',
    'Submitted',
    'Resolved',
    'SLA Started',
    'SLA Due',
    'SLA Status',
    'SLA Breached',
    'SLA Completed',
    'MTTR (Minutes)',
    'CSAT Score',
    'CSAT Label',
    'CSAT Submitted',
  ]
  const rowValues = (r: TicketsOverviewRowDetail) => {
    const slaStatus = slaStatusForDisplay(r)
    return [
    r.id,
    escapeCsvCell(r.request_number ?? ''),
    escapeCsvCell(r.service_category ?? ''),
    escapeCsvCell(r.category_name ?? ''),
    escapeCsvCell(r.sub_category_name ?? ''),
    escapeCsvCell(r.item_name ?? ''),
    escapeCsvCell(r.status_label ?? r.status_code ?? ''),
    escapeCsvCell(r.assigned_to?.trim() || '—'),
    escapeCsvCell(getTicketPriorityValue(r)),
    escapeCsvCell(formatSeverityLabel(getTicketSeverityValue(r))),
    escapeCsvCell(divisionCodeForCsv(r), { restricted: true }),
    escapeCsvCell(r.location ?? ''),
    escapeCsvCell(r.contact_name ?? ''),
    escapeCsvCell(r.created_by_name ?? ''),
    escapeCsvCell(r.contact_email ?? ''),
    escapeCsvCell(r.contact_number ?? ''),
    escapeCsvCell(formatDatetime(r.created_at)),
    escapeCsvCell(formatDatetime(r.submitted_at)),
    escapeCsvCell(formatDatetime(r.resolved_at)),
    escapeCsvCell(formatDatetime(r.sla_clock_started_at)),
    escapeCsvCell(formatDatetime(r.sla_clock_due_at)),
    slaStatus === DISPLAY_EMPTY ? '' : slaStatus,
    r.sla_breached != null ? (r.sla_breached ? 'Yes' : 'No') : (r.sla_clock_breached_at ? 'Yes' : 'No'),
    escapeCsvCell(formatDatetime(r.sla_clock_completed_at)),
    r.net_recovery_minutes != null ? r.net_recovery_minutes.toFixed(2) : '',
    escapeCsvCell(r.csat_score ?? ''),
    escapeCsvCell(csatLabelForCsv(r), { restricted: true }),
    escapeCsvCell(formatDatetime(r.csat_submitted_at)),
  ]
  }
  const lines = rows.map((r) => rowValues(r).join(','))
  return [headers.join(','), ...lines].join('\r\n')
}

function agentsTableToCSV(table: TicketsOverviewRowAgent[]): string {
  const headers = ['Agent ID', 'Agent Name', 'Open', 'Resolved', 'Total']
  const rows = table.map((r) =>
    [r.agent_id, escapeCsvCell(r.agent_name), r.open_count, r.resolved_count, r.total_count].join(',')
  )
  return [headers.join(','), ...rows].join('\r\n')
}

function mttrTableToCSV(rows: TicketsOverviewRowMttr[]): string {
  const headers = [
    'Ticket Key / ID',
    'Requestor',
    'Priority',
    'Severity',
    'Summary',
    'Created Date',
    'Resolved Date',
    'Paused Minutes',
    'Net Recovery Time (Minutes)',
    'Net Recovery Time (Hours)',
  ]
  const lines = rows.map((r) =>
    [
      escapeCsvCell(r.request_number ?? ''),
      escapeCsvCell([r.requestor, r.requestor_email ? `<${r.requestor_email}>` : ''].filter(Boolean).join(' ')),
      escapeCsvCell(r.priority ?? ''),
      escapeCsvCell(formatSeverityLabel(r.sla_severity_label ?? r.severity)),
      escapeCsvCell(r.summary ?? ''),
      escapeCsvCell(formatDatetime(r.created_at)),
      escapeCsvCell(formatDatetime(r.resolved_at)),
      r.sla_clock_total_paused_minutes ?? (r as any).total_paused_minutes ?? (r as any).paused_minutes ?? '',
      r.net_recovery_minutes ?? '',
      r.net_recovery_hours ?? '',
    ].join(',')
  )
  return [headers.join(','), ...lines].join('\r\n')
}

function downloadCSV(content: string, filename: string) {
  // UTF-8 BOM so Excel on Windows opens division/CSAT text correctly
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

export default function AnalyticsDashboardPage() {
  const defaultRange = getDefaultDateRange()
  const [categories, setCategories] = useState<CategoryOption[]>([])
  const [ticketStatuses, setTicketStatuses] = useState<TicketStatusOption[]>([])
  const [filters, setFilters] = useState<TicketsOverviewParams>({
    category_id: null,
    ticket_status_id: null,
    date_from: defaultRange.date_from,
    date_to: defaultRange.date_to,
    statistics_type: 'tickets',
  })
  const [data, setData] = useState<TicketsOverviewResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  useEffect(() => {
    apiClient
      .get('/category-management/categories/dropdown')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.data ?? []
        setCategories(
          list.map((s: { id: number; name: string; code: string }) => ({ id: s.id, name: s.name, code: s.code }))
        )
      })
      .catch(() => {})
    apiClient
      .get('/service-catalog/ticket-statuses', { params: { per_page: 200 } })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.data ?? []
        setTicketStatuses(
          list.map((s: { id: number; code: string; label: string }) => ({ id: s.id, code: s.code, label: s.label }))
        )
      })
      .catch(() => {})
  }, [])

  const fetchOverview = useCallback(async () => {
    setIsLoading(true)
    setHasFetched(true)
    setFetchError(null)
    try {
      const result = await analyticsService.getTicketsOverview(filters)
      setData(result)
    } catch (error) {
      console.error('Error fetching tickets overview:', error)
      setData(null)
      setFetchError('Could not load ticket details. Check that the API is running and try again.')
    } finally {
      setIsLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchOverview()
  }, [])

  const handleApply = () => {
    fetchOverview()
  }

  const chartData =
    data?.chart?.labels?.map((label, i) => ({
      label,
      value: data.chart.data[i] ?? 0,
    })) ?? []

  const handleDownloadSummaryCSV = () => {
    if (!data?.chart?.labels?.length) return
    const csv = chartSummaryToCSV(data.chart.labels, data.chart.data)
    downloadCSV(csv, `tickets-summary-${filters.date_from}-${filters.date_to}.csv`)
  }

  const handleDownloadDetailsCSV = () => {
    if (!data) return
    if (data.statistics_type === 'agents') {
      if (!(data.table as TicketsOverviewRowAgent[]).length) return
      const csv = agentsTableToCSV(data.table as TicketsOverviewRowAgent[])
      downloadCSV(csv, `tickets-agents-${filters.date_from}-${filters.date_to}.csv`)
    } else if (data.statistics_type === 'mttr') {
      if (!(data.table as TicketsOverviewRowMttr[]).length) return
      const csv = mttrTableToCSV(data.table as TicketsOverviewRowMttr[])
      downloadCSV(csv, `tickets-mttr-${filters.date_from}-${filters.date_to}.csv`)
    } else {
      if (!(data.table as TicketsOverviewRowDetail[]).length) return
      const csv = detailsTableToCSV(data.table as TicketsOverviewRowDetail[])
      downloadCSV(csv, `tickets-details-${filters.date_from}-${filters.date_to}.csv`)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={4} align="stretch" flex="1" overflowY="auto" minH={0}>
          <Card>
            <CardHeader>
              <Heading size="md">Analytics Overview</Heading>
              <Text fontSize="sm" color="gray.500">
                Ticket statistics by category, date range, and view (Tickets or Agents)
              </Text>
            </CardHeader>
            <CardBody pt={0}>
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
                gap={4}
                alignItems="end"
              >
                <FormControl>
                  <FormLabel>Service Category</FormLabel>
                  <Select
                    value={filters.category_id ?? ''}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        category_id: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                  >
                    <option value="">All</option>
                    {categories.map((st) => (
                      <option key={st.id} value={st.id}>
                        {st.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={filters.ticket_status_id ?? ''}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        ticket_status_id: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                  >
                    <option value="">All</option>
                    {ticketStatuses.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Date from</FormLabel>
                  <input
                    type="date"
                    value={filters.date_from ?? ''}
                    onChange={(e) => setFilters((prev) => ({ ...prev, date_from: e.target.value || undefined }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid',
                      borderColor: 'var(--chakra-colors-gray-200)',
                      borderRadius: 'var(--chakra-radii-md)',
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Date to</FormLabel>
                  <input
                    type="date"
                    value={filters.date_to ?? ''}
                    onChange={(e) => setFilters((prev) => ({ ...prev, date_to: e.target.value || undefined }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid',
                      borderColor: 'var(--chakra-colors-gray-200)',
                      borderRadius: 'var(--chakra-radii-md)',
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Data Statistics</FormLabel>
                  <Select
                    value={filters.statistics_type ?? 'tickets'}
                    onChange={(e) =>
                      setFilters((prev) => {
                        const nextType = e.target.value as 'tickets' | 'agents' | 'mttr'
                        // MTTR is based on resolved_at; widen the default window so historical
                        // resolved tickets are visible without manual date tweaking.
                        if (
                          nextType === 'mttr' &&
                          prev.date_from === defaultRange.date_from &&
                          prev.date_to === defaultRange.date_to
                        ) {
                          const mttrRange = getMttrDefaultDateRange()
                          return {
                            ...prev,
                            statistics_type: nextType,
                            date_from: mttrRange.date_from,
                            date_to: mttrRange.date_to,
                          }
                        }

                        return {
                          ...prev,
                          statistics_type: nextType,
                        }
                      })
                    }
                  >
                    <option value="tickets">Tickets</option>
                    <option value="agents">Agents</option>
                    <option value="mttr">MTTR Report</option>
                  </Select>
                </FormControl>
              </Grid>
              <HStack mt={4}>
                <Button colorScheme="blue" onClick={handleApply} isLoading={isLoading}>
                  Apply
                </Button>
              </HStack>
            </CardBody>
          </Card>

          <Card flexShrink={0}>
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Heading size="sm">Tickets over time</Heading>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDownloadSummaryCSV}
                  isDisabled={!data?.chart?.labels?.length}
                >
                  Download CSV
                </Button>
              </Flex>
            </CardHeader>
            <CardBody pt={0}>
              {isLoading && !data ? (
                <Skeleton height="280px" width="100%" />
              ) : chartData.length > 0 ? (
                <Box h="280px" w="100%">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#3182CE"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        name="Tickets"
                      />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Text color="gray.500" py={8}>
                  No chart data for the selected filters.
                </Text>
              )}
            </CardBody>
          </Card>

          {data?.statistics_type === 'mttr' && data.mttr_summary ? (
            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} flexShrink={0} w="100%" minW={0}>
              <Card bg="white" borderRadius="md" boxShadow="sm">
                <CardBody>
                  <Heading size="xs" mb={2}>Average MTTR</Heading>
                  <Text fontSize="2xl" fontWeight="bold">{formatMttrSummaryHours(data.mttr_summary.average_mttr_hours)}</Text>
                  <Text fontSize="sm" color="gray.500">Based on resolved tickets in range</Text>
                </CardBody>
              </Card>
              <Card bg="white" borderRadius="md" boxShadow="sm">
                <CardBody>
                  <Heading size="xs" mb={2}>Average MTTR (minutes)</Heading>
                  <Text fontSize="2xl" fontWeight="bold">{formatMttrSummaryMinutes(data.mttr_summary.average_mttr_minutes)}</Text>
                  <Text fontSize="sm" color="gray.500">Mean time to resolution</Text>
                </CardBody>
              </Card>
              <Card bg="white" borderRadius="md" boxShadow="sm">
                <CardBody>
                  <Heading size="xs" mb={2}>Resolved Tickets</Heading>
                  <Text fontSize="2xl" fontWeight="bold">{data.mttr_summary.ticket_count}</Text>
                  <Text fontSize="sm" color="gray.500">Tickets included in MTTR</Text>
                </CardBody>
              </Card>
            </Grid>
          ) : null}

          <Card flexShrink={0}>
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Heading size="sm">
                  {data?.statistics_type === 'agents'
                    ? 'Agent statistics'
                    : data?.statistics_type === 'mttr'
                      ? 'MTTR Extraction and Service Restoration Report'
                    : 'Ticket details (request, status, SLA clock)'}
                </Heading>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDownloadDetailsCSV}
                  isDisabled={!data?.table?.length}
                >
                  Download CSV
                </Button>
              </Flex>
            </CardHeader>
            <CardBody pt={0}>
              {isLoading && !data ? (
                <Skeleton height="200px" width="100%" />
              ) : data?.statistics_type === 'agents' ? (
                <TableContainer overflowX="auto">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Agent</Th>
                        <Th>Open</Th>
                        <Th>Resolved</Th>
                        <Th>Total</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(data.table as TicketsOverviewRowAgent[]).map((row) => (
                        <Tr key={row.agent_id}>
                          <Td fontWeight="medium">{row.agent_name}</Td>
                          <Td>{row.open_count}</Td>
                          <Td>{row.resolved_count}</Td>
                          <Td>{row.total_count}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {!data.table?.length && (
                    <Text color="gray.500" py={4} textAlign="center">
                      No agent data
                    </Text>
                  )}
                </TableContainer>
              ) : data?.statistics_type === 'mttr' ? (
                <TableContainer overflowX="auto">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Ticket Key / ID</Th>
                        <Th>Requestor</Th>
                        <Th>Priority</Th>
                        <Th>Severity</Th>
                        <Th>Summary</Th>
                        <Th>Created Date</Th>
                        <Th>Resolved Date</Th>
                        <Th isNumeric>Paused Minutes</Th>
                        <Th isNumeric>Net Recovery (Minutes)</Th>
                        <Th isNumeric>Net Recovery (Hours)</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(data.table as TicketsOverviewRowMttr[]).map((r) => (
                        <Tr key={r.id}>
                          <Td fontWeight="medium">{r.request_number || '—'}</Td>
                          <Td>
                            {r.requestor || '—'}
                            {r.requestor_email ? ` (${r.requestor_email})` : ''}
                          </Td>
                          <Td>{r.priority || '—'}</Td>
                          <Td>{formatSeverityLabel(r.sla_severity_label ?? r.severity) || '—'}</Td>
                          <Td maxW="360px" whiteSpace="normal">{r.summary || '—'}</Td>
                          <Td whiteSpace="nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</Td>
                          <Td whiteSpace="nowrap">{r.resolved_at ? new Date(r.resolved_at).toLocaleDateString() : '—'}</Td>
                          <Td isNumeric>
                            {r.sla_clock_total_paused_minutes ?? (r as any).total_paused_minutes ?? (r as any).paused_minutes ?? '—'}
                          </Td>
                          <Td isNumeric>{r.net_recovery_minutes ?? '—'}</Td>
                          <Td isNumeric>{r.net_recovery_hours ?? '—'}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {!data.table?.length && (
                    <Text color="gray.500" py={4} textAlign="center">
                      No MTTR data for this resolved date range. Try expanding Date from to an earlier date.
                    </Text>
                  )}
                </TableContainer>
              ) : (
                <TableContainer overflowX="auto">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Request #</Th>
                        <Th>Service Category</Th>
                        <Th>Category</Th>
                        <Th>Sub-Category</Th>
                        <Th>Item</Th>
                        <Th>Status</Th>
                        <Th>Technician</Th>
                        <Th>Priority</Th>
                        <Th>Severity</Th>
                        <Th>Division code</Th>
                        <Th>Location</Th>
                        <Th>Contact Name</Th>
                        <Th>Created By</Th>
                        <Th>Contact Email</Th>
                        <Th>Contact Number</Th>
                        <Th>Created</Th>
                        <Th>Submitted</Th>
                        <Th>Resolved</Th>
                        <Th>SLA started</Th>
                        <Th>SLA due</Th>
                        <Th>SLA status</Th>
                        <Th>SLA breached</Th>
                        <Th>SLA completed</Th>
                        <Th isNumeric>MTTR (Minutes)</Th>
                        <Th>CSAT</Th>
                        <Th>CSAT submitted</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(data?.table ?? []).map((row) => {
                        const r = row as TicketsOverviewRowDetail
                        const isBreached = r.sla_breached != null ? r.sla_breached : !!r.sla_clock_breached_at
                        const csatCell = csatLabelForDisplay(r)
                        return (
                          <Tr key={r.id}>
                            <Td fontWeight="medium">{r.request_number ?? '—'}</Td>
                            <Td>{r.service_category ?? '—'}</Td>
                            <Td>{r.category_name ?? '—'}</Td>
                            <Td>{r.sub_category_name ?? '—'}</Td>
                            <Td>{r.item_name ?? '—'}</Td>
                            <Td>{r.status_label ?? r.status_code ?? '—'}</Td>
                            <Td>{r.assigned_to?.trim() || '—'}</Td>
                            <Td>{getTicketPriorityValue(r) || '—'}</Td>
                            <Td>{formatSeverityLabel(getTicketSeverityValue(r)) || '—'}</Td>
                            <Td>{divisionCodeForDisplay(r)}</Td>
                            <Td maxW="200px" whiteSpace="normal">{r.location?.trim() ? r.location : '—'}</Td>
                            <Td>{r.contact_name || '—'}</Td>
                            <Td>{r.created_by_name || '—'}</Td>
                            <Td>{r.contact_email || '—'}</Td>
                            <Td>{r.contact_number || '—'}</Td>
                            <Td whiteSpace="nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</Td>
                            <Td whiteSpace="nowrap">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '—'}</Td>
                            <Td whiteSpace="nowrap">{r.resolved_at ? new Date(r.resolved_at).toLocaleDateString() : '—'}</Td>
                            <Td whiteSpace="nowrap">{r.sla_clock_started_at ? new Date(r.sla_clock_started_at).toLocaleString() : '—'}</Td>
                            <Td whiteSpace="nowrap">{r.sla_clock_due_at ? new Date(r.sla_clock_due_at).toLocaleString() : '—'}</Td>
                            <Td>{slaStatusForDisplay(r)}</Td>
                            <Td color={isBreached ? 'red.500' : 'green.500'} fontWeight="medium">{isBreached ? 'Yes' : 'No'}</Td>
                            <Td whiteSpace="nowrap">{r.sla_clock_completed_at ? new Date(r.sla_clock_completed_at).toLocaleString() : '—'}</Td>
                            <Td isNumeric>{r.net_recovery_minutes != null ? r.net_recovery_minutes.toFixed(2) : '—'}</Td>
                            <Td>{csatCell || '—'}</Td>
                            <Td whiteSpace="nowrap">
                              {r.csat_submitted_at ? new Date(r.csat_submitted_at).toLocaleString() : '—'}
                            </Td>
                          </Tr>
                        )
                      })}
                    </Tbody>
                  </Table>
                  {fetchError && (
                    <Text color="red.500" py={4} textAlign="center" fontSize="sm">
                      {fetchError}
                    </Text>
                  )}
                  {!fetchError && !data?.table?.length && (
                    <Text color="gray.500" py={4} textAlign="center">
                      No ticket details for the selected filters. Try a wider date range or different category.
                    </Text>
                  )}
                </TableContainer>
              )}
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}
